import React from "react";
import { getColumnStyle } from "./utils";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";
import type { BaseEntity } from "./types";

/**
 * Extract and organize state from the setup result.
 * This includes destructuring state values and creating memoized handlers.
 */
export function extractSimpleTableState<TData extends BaseEntity = BaseEntity>(
  setup: SimpleTableSetupResult<TData>
) {
  const {
    state,
    paginatedData,
    tableHooks: { containerRef, tableHeaderScrollRef, tableBodyRef },
  } = setup;

  // Extract state values for easier access
  const {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    sortColumns,
    setSortColumns,
    selectedItems,
    setSelectedItems,
    showFloatingBulkActions,
    setShowFloatingBulkActions,
    columnWidths,
    setColumnWidths,
    columnOrder,
    setColumnOrder,
    draggedColumn,
    setDraggedColumn,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
  } = state;

  // Create handlers that include paginatedData
  const handleSelectAll = React.useMemo(() => {
    // This will be populated by callbacks setup
    return (selected: boolean, data: any[]) => {
      // Placeholder - actual implementation in callbacks
    };
  }, []);

  const handleBulkAction = React.useMemo(() => {
    // This will be populated by callbacks setup
    return (actionId: string, data: any[]) => {
      // Placeholder - actual implementation in callbacks
    };
  }, []);

  // Wrapper for scroll handlers
  const handleBodyScroll = React.useCallback(() => {
    if (tableBodyRef?.current && tableHeaderScrollRef?.current) {
      tableHeaderScrollRef.current.scrollLeft = tableBodyRef.current.scrollLeft;
    }
  }, [tableBodyRef, tableHeaderScrollRef]);

  const handleHeaderScroll = React.useCallback(() => {
    if (tableBodyRef?.current && tableHeaderScrollRef?.current) {
      tableBodyRef.current.scrollLeft = tableHeaderScrollRef.current.scrollLeft;
    }
  }, [tableBodyRef, tableHeaderScrollRef]);

  // Get column style callback (expands column when its cell is being edited)
  const getColumnStyleCallback = React.useCallback(
    (fieldId: string, fieldConfig: any) =>
      getColumnStyle(fieldId, fieldConfig, columnWidths, editingCell),
    [columnWidths, editingCell]
  );

  return {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    sortColumns,
    setSortColumns,
    selectedItems,
    setSelectedItems,
    showFloatingBulkActions,
    setShowFloatingBulkActions,
    columnWidths,
    setColumnWidths,
    columnOrder,
    setColumnOrder,
    draggedColumn,
    setDraggedColumn,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
    handleSelectAll,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    getColumnStyleCallback,
  };
}

export type SimpleTableStateSetupResult = ReturnType<typeof extractSimpleTableState>;
